import { CatalogCardProps } from "@/types/component";
import { Clock, User, Calendar } from "@/components/icons";
import Chip from "@/components/ui/chip";
import Button from "@/components/ui/button";
import FileImage from "@/components/ui/file-image";

import "./index.scss";

import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Eğitim katalog kartı.
 * Liste/grid görünümünde eğitim özeti gösterir.
 * Detay açıklama (description) detay sayfasında gösterilir, kartta yer almaz.
 */
const DESCRIPTION_MAX_LENGTH = 120;

const CatalogCard: React.FC<CatalogCardProps> = ({
  imageFileId,
  fallbackImageUrl,
  image,
  title,
  description,
  educatorName,
  duration,
  deliveryType,
  category,
  statusType = "simple",
  statusText,
  statusIcon,
  deadline,
  participantCount,
  maxParticipants,
  dateTime,
  buttonLabel,
  onButtonClick,
  buttonDisabled,
  onCardClick,
  isRequired,
  className,
}) => {
  const { t } = useTranslation();
  const getDeliveryTypeColor = ():
    | "default"
    | "error"
    | "info"
    | "primary"
    | "secondary"
    | "success"
    | "warning" => {
    const type = deliveryType.toLowerCase();
    if (type === "yüz yüze" || type === "face to face") return "warning";
    if (type === "online") return "info";
    if (type === "canlı" || type === "live") return "success";
    if (type === "üniteli" || type === "hybrid" || type === "hibrit")
      return "info";
    return "warning";
  };

  const renderStatus = () => {
    switch (statusType) {
      case "deadline":
        return (
          <div className="catalog-card__status catalog-card__status--deadline">
            <Calendar width={16} height={16} color="#707A8F" />
            <span>Son Tarih: {deadline}</span>
          </div>
        );

      case "participant":
        return (
          <div className="catalog-card__status-group">
            <div className="catalog-card__status catalog-card__status--participant">
              <User width={18} height={18} color="#707A8F" />
              <span>
                {participantCount}/{maxParticipants}
              </span>
            </div>
            {dateTime && (
              <div className="catalog-card__status catalog-card__status--datetime">
                <Calendar width={18} height={18} color="#707A8F" />
                <span>{dateTime}</span>
              </div>
            )}
          </div>
        );

      case "simple":
      default:
        return (
          <div className="catalog-card__status">
            {statusIcon || <User width={16} height={16} color="#72808F" />}
            <span>{statusText}</span>
          </div>
        );
    }
  };

  const handleCardClick = () => {
    onCardClick?.();
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonDisabled) return;
    e.stopPropagation();
    onButtonClick?.(e);
  };

  const isCardClickable = !!onCardClick;

  return (
    <div
      className={`catalog-card ${className || ""} ${isCardClickable ? "catalog-card--clickable" : ""}`}
      onClick={isCardClickable ? handleCardClick : undefined}
      role={isCardClickable ? "button" : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
      onKeyDown={
        isCardClickable
          ? (e) => e.key === "Enter" && onCardClick?.()
          : undefined
      }
    >
      <div className="catalog-card__image">
        <FileImage
          fileId={imageFileId}
          fallbackSrc={fallbackImageUrl ?? image}
          alt={title}
          objectFit="cover"
        />
      </div>

      <div className="catalog-card__content">
        <div className="catalog-card__meta">
          <div className="catalog-card__tags">
            <Chip type={getDeliveryTypeColor()} variant="filled" size="small">
              {deliveryType}
            </Chip>
            <Chip type="default" variant="outlined" size="small">
              {category}
            </Chip>
            {isRequired && (
              <Chip type="error" variant="filled" size="small">
                {t("clientHero.mandatory")}
              </Chip>
            )}
          </div>
          <div className="catalog-card__duration">
            <Clock width={18} height={18} color="#72808F" />
            <span>{duration}</span>
          </div>
        </div>
        <div className="catalog-card__title-group">
          <h3 className="catalog-card__title">{title}</h3>
          {description && (
            <p className="catalog-card__description" title={description}>
              {description.length > DESCRIPTION_MAX_LENGTH
                ? `${description.slice(0, DESCRIPTION_MAX_LENGTH)}...`
                : description}
            </p>
          )}
          <p className="catalog-card__educator">{educatorName}</p>
        </div>

        {renderStatus()}

        <div className="catalog-card__action">
          <Button
            variant="outlined"
            color="default"
            size="medium"
            onClick={handleButtonClick}
            disabled={buttonDisabled}
            className="catalog-card__button"
          >
            {buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CatalogCard;
